import React, { useEffect, useState, useContext, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Unstable_Grid2";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CSDataTable from "../../components/CSDataTable";
import { CSDataTableColumns } from "../../modal/CSDataTable";
import { convertToDateMonthFormat } from "../../utils/ConvertDate";
import PolicyContext from "../../context/policy/PolicyContext";
import { DisplayResponseTests } from "./components/DisplayResponseTests";
import DialogBoxComponent from "../../components/DialogBoxComponent";
import { fetchIRPTest, uploadFilesIRPTest } from "./helper";
import { useRef } from "react";
import { Box, CircularProgress } from "@mui/material";
import { IRPTestList } from "./modal";
import { useIncidentResponsePlanContext } from "../../context/IncidentResponsePlan/IncidentPlanResponceContext";
import { useUserContext } from "../../context/auth/UserContext";
import DialogBoxForm from "../FileRepository/Component/DialogBoxFileRepositoryForm";
import DialogBoxFormIRPTest from "./components/DialogFormIRPTest";
import { useAccessRights } from "../../utils/useAccessRights";

export const IncidentResponseTests = () => {
  const navigate = useNavigate();
  const incidentResponseTestAccess = useAccessRights("Incident Response Tests");

  const [testList, setTestList] = useState<IRPTestList[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openImportDialog: false,
    openSuccessDialogBox: false,
  });
  const [dialogForm, setDialogForm] = useState({
    title: "",
    action: "",
    actionCancel: "",
    openDialogUpload: false,
  });
  // const policyContext = useContext(PolicyContext);
  // const { setSelectedPolicy } = policyContext;
  const {
    selectedIRPTestPreview,
    setSelectedIRPTestPreview,
    setIsFileRefresh,
    setIRPTestDetails,
  } = useIncidentResponsePlanContext();
  const { userId, companyProfileID } = useUserContext();
  const open = Boolean(anchorEl);
  const [isLoading, setIsLoading] = useState(true);
  const hasFetched = useRef(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const displayAddTest = () => {
    // setSelectedPolicy && setSelectedPolicy([]);
    setIRPTestDetails({});
    navigate("/incident-response-test-form");
  };

  const displayImportTest = () => {
    setDialogState({
      title: "Import Incident Response Tests",
      body: `By importing the template plan, you will overwrite the content in any existing version 1.0 plan for your organization.`,
      action: "Import",
      actionCancel: "Cancel",
      openImportDialog: true,
      openSuccessDialogBox: false,
    });
  };

  const handleTestImportCancel = () => {
    setDialogState({
      ...dialogState,
      openImportDialog: false,
      openSuccessDialogBox: false,
    });
  };

  const columns: CSDataTableColumns[] = [
    {
      accessorKey: "testName",
      header: "Name",
      size: "80px",
    },
    {
      accessorKey: "testTypeExtract",
      header: "Type",
      size: "60px",
    },
    {
      accessorKey: "incidentDate",
      header: "Incident Date",
      size: "80px",
    },
    {
      accessorKey: "resolutionDate",
      header: "Resolution Date",
      size: "80px",
    },
  ];

  const handleSelectPreview = (selectedRows: any) => {
    setSelectedIRPTestPreview(selectedRows[0]);
  };

  const getIRPTestList = async () => {
    setIsLoading(true);
    try {
      const res = await fetchIRPTest(companyProfileID);
      const formattedList = res.map((test: any) => ({
        ...test,
        rowId: test.testID,
        testTypeExtract: test.testTypes?.testTypeName || null,
        incidentDate: convertToDateMonthFormat(test.incidentDate),
        resolutionDate: convertToDateMonthFormat(test.resolutionDate),
      }));

      setTestList(formattedList);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadFileClick = () => {
    setDialogForm({
      title: "Upload Files",
      action: "Upload",
      actionCancel: "Cancel",
      openDialogUpload: true,
    });
  };

  const handleCloseDialogBoxForm = () => {
    setDialogForm({
      ...dialogState,
      openDialogUpload: false,
    });
  };

  const handleUploadFileApi = async (file: File[]) => {
    try {
      const res: any = await uploadFilesIRPTest(
        file,
        userId,
        selectedIRPTestPreview.testID
      );
      // console.log("res", res);

      if (res.succeeded) {
        // console.log("handleUploadFileApi ", "res", res);
        setDialogState({
          title: "Success",
          body: "File(s) has been successfully uploaded",
          action: "OK",
          actionCancel: "",
          openImportDialog: false,
          openSuccessDialogBox: true,
        });
      } else {
        setDialogState({
          title: "Error",
          body: "An error occurred while uploading files.",
          action: "OK",
          actionCancel: "",
          openImportDialog: false,
          openSuccessDialogBox: true,
        });
      }
    } finally {
      setIsFileRefresh(true);
    }
  };

  useEffect(() => {
    setSelectedIRPTestPreview(null);
    setIRPTestDetails({});
    if (!hasFetched.current) {
      getIRPTestList();
      hasFetched.current = true;
    }
  }, []);

  return (
    <Fragment>
      <Grid container sx={{ height: "100%", width: "100%" }}>
        <Grid
          xs={6}
          style={{
            padding: "1rem",
            margin: "1rem 0",
            background: "#FFF",
            borderRadius: "2%",
          }}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid xs={3}>
              <h3 style={{ fontWeight: "400" }}>
                Incident Response Tests ({testList.length})
              </h3>
            </Grid>
            <Grid>
              <Button
                size="small"
                variant="contained"
                className="table-btn"
                onClick={displayAddTest}
                disabled={!incidentResponseTestAccess?.isCreate || isLoading}
              >
                Create New Test
              </Button>
              <Button
                size="small"
                variant="contained"
                className="table-btn"
                onClick={handleUploadFileClick}
                disabled={
                  !selectedIRPTestPreview ||
                  !incidentResponseTestAccess?.isUpload ||
                  isLoading
                }
              >
                Upload Files
              </Button>
              {/* <IconButton aria-label="search">
                <SearchIcon />
              </IconButton> */}
              {/* <IconButton aria-label="menu" onClick={handleClick}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem>PlayBook</MenuItem>
                <MenuItem>Tests</MenuItem>
              </Menu> */}
            </Grid>
          </Grid>
          {isLoading ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <CSDataTable
              rows={testList}
              columns={columns}
              multiRowSelection={false}
              previewRecord={selectedIRPTestPreview}
              // handleSelectPreview={(selectedRecord) =>
              //   handleSelectPreview(selectedRecord)
              // }
              handleSelectRow={(selectedRows) =>
                handleSelectPreview(selectedRows)
              }
            />
          )}
        </Grid>

        <Grid
          xs
          style={{
            padding: "1rem",
            margin: "1rem",
            background: "#FFF",
            borderRadius: "2%",
          }}
        >
          <DisplayResponseTests
            loadingPage={isLoading}
            refreshList={() => getIRPTestList()}
          />
        </Grid>
      </Grid>

      {/* //Upload Files Dialog */}
      {
        <DialogBoxFormIRPTest
          openDialogBox={dialogForm.openDialogUpload}
          dialogTitle={dialogForm.title}
          dialogAction={dialogForm.action}
          dialogActionCancel={dialogForm.actionCancel}
          handleFunction={handleUploadFileApi}
          handleCancel={handleCloseDialogBoxForm}
        />
      }
      {
        <DialogBoxComponent
          openDialogBox={dialogState.openImportDialog}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogActionCancel={dialogState.actionCancel}
          dialogBody={dialogState.body}
          handleFunction={handleTestImportCancel}
          handleCancel={handleTestImportCancel}
        />
      }
    </Fragment>
  );
};
